import { store, updateSidePanel, closeSidePanel, activeRightStack } from '@/app/store';
import { resetAlertModal } from '@/components/AlertModalRoot/alertModalService';
import { clearStarredMessagesByChat } from '@/features/chats/model';
import { clearMessages, getConversationMessages } from '@/features/chats/model/conversation';
import { clearRecentChat, deleteRecentChat, updateArchiveStatus } from '@/features/chats/model/recentChat';
import { isGroupChat } from '@/features/chats/utils';
import { updateUserInfo } from '@/features/users/model';
import { archiveChat, clearChat, deleteChat, muteChat, reportUserOrGroup, userDeleteGroup } from '@/infrastructure/sdk';
import recentChatIdb from '@/infrastructure/sync/db/RecentChatIdb';
import { i18n } from '@/localization/i18n';
import { blockOfflineAction, consoleLog, getUserIdFromJid } from '@/shared/utils';
import { showErrorToast, showSuccessToast } from '@/shared/utils/toastMessage';

export const clearChatAction = async (activeChatJid, onClose) => {
   if (blockOfflineAction()) return;
   const userId = getUserIdFromJid(activeChatJid);
   const chatMessages = getConversationMessages(userId);

   if (!chatMessages?.length) {
      showErrorToast(i18n.t('toast.NO_MESSAGES_TO_CLEAR'));
      resetAlertModal('clearChatModal');
      return onClose();
   }

   try {
      const res = await clearChat(activeChatJid, true, '');
      if (res.statusCode === 200) {
         store.dispatch(clearMessages({ fromUserId: userId }));
         store.dispatch(clearRecentChat({ fromUserId: userId }));
         store.dispatch(clearStarredMessagesByChat({ chatJid: activeChatJid }));
         onClose();
      }
   } catch (err) {
      consoleLog(err, 'err');
   } finally {
      resetAlertModal('clearChatModal');
   }
};

export const deleteChatAction = async (activeChatJid, onClose) => {
   try {
      let response = null;
      if (blockOfflineAction()) return;

      const userId = getUserIdFromJid(activeChatJid);
      const isGroup = isGroupChat(activeChatJid);

      if (isGroup) {
         response = await userDeleteGroup(activeChatJid);
      } else {
         response = await deleteChat(activeChatJid);
      }

      if (response?.statusCode === 200) {
         // Remove the deleted chat (group or 1:1) from local/redux state immediately,
         // instead of relying on a refresh or an incidental side-effect elsewhere.
         store.dispatch(clearMessages({ fromUserId: userId }));
         store.dispatch(deleteRecentChat({ fromUserId: userId }));
         store.dispatch(clearStarredMessagesByChat({ chatJid: activeChatJid }));
         await recentChatIdb.deleteRecentChat(userId);

         // If the deleted chat is the one currently open, navigate the user away from it.
         const currentRightStack = store.getState()?.navigationData?.activeRightStack;
         const isCurrentlyOpen =
            currentRightStack?.activeChatJid === activeChatJid || currentRightStack?.activeChatId === userId;

         if (isCurrentlyOpen) {
            store.dispatch(activeRightStack({ activeStack: '', activeChatId: '', activeChatJid: '' }));
            store.dispatch(closeSidePanel());
         }
      } else {
         showErrorToast(response?.message || i18n.t('toast.ACTION_FAILED'));
      }

      onClose?.();
      return response;
   } catch (err) {
      consoleLog(err, 'err');
   }
};

const reportMembers = 5;

const getMessagesForReport = (chatId, messageId = '') => {
   const chatMessages = getConversationMessages(chatId);
   if (!chatMessages?.length) return [];

   // Expand groupedMedia groups — wrappers have no msgBody, children do
   const flatMessages = chatMessages.flatMap(m => (m.type === 'groupedMedia' ? m.messages || [] : [m]));

   const startIndex = messageId ? flatMessages.findIndex(msg => msg.msgId === messageId) : 0;
   if (startIndex === -1) return [];

   return flatMessages.slice(startIndex, startIndex + reportMembers).reduce((acc, item) => {
      if (!item?.msgBody || Object.keys(item.msgBody).length === 0) return acc;

      const isEdited = item.editedStatus === 1;
      const baseMessage = item.msgBody?.media?.caption ?? item.msgBody?.message ?? '';

      acc.push({
         msgId: item.msgId ?? '',
         message: isEdited ? `${i18n.t('message.EDITED')} - ${baseMessage}` : baseMessage,
         msgType: item.msgBody?.message_type || item.msgType || '',
         filename: item.msgBody?.media?.fileName ?? '',
         timestamp: item.timestamp,
         publisherId: item.publisherId ?? item.fromUserId,
      });

      return acc;
   }, []);
};

export const reportChatAction = async (activeChatJid, onClose) => {
   try {
      if (blockOfflineAction()) return;
      const activeChatId = getUserIdFromJid(activeChatJid);
      const chatType = isGroupChat(activeChatJid) ? 'groupchat' : 'chat';
      const reportData = getMessagesForReport(activeChatId);

      if (!reportData.length) {
         showErrorToast(i18n.t('toast.NO_MESSAGES_TO_REPORT'));
         return onClose();
      }

      const res = await reportUserOrGroup(activeChatJid, chatType, reportData);
      if (res.statusCode === 200) {
         showSuccessToast(i18n.t('toast.REPORT_SENT_SUCCESS'));
         onClose();
      }
   } catch (err) {
      consoleLog(err, 'err');
   }
};

export const openMediaGallery = () => {
   store.dispatch(
      updateSidePanel({
         id: 'media-gallery',
         isOpen: true,
         title: '',
      }),
   );
};

export const chatMuteAction = async (activeChatJid, muteStatus) => {
   try {
      if (blockOfflineAction()) return;
      if (!activeChatJid) return;
      const response = await muteChat(activeChatJid, muteStatus);
      if (response.statusCode === 200) {
         store.dispatch(
            updateUserInfo({
               userId: getUserIdFromJid(activeChatJid),
               userData: { isMuted: muteStatus },
            }),
         );
      }
   } catch (error) {
      consoleLog(error, 'err');
   }
};

export const archiveChatAction = async ({ jid, status, toast = true }) => {
   try {
      if (blockOfflineAction()) return;
      if (!jid) return;
      const isArchived = status !== 1; // toggle
      const response = await archiveChat(jid, isArchived);
      if (response?.statusCode === 200) {
         const fromUserId = getUserIdFromJid(jid);
         store.dispatch(updateArchiveStatus({ fromUserId, isArchived }));
         if (toast) {
            showSuccessToast(isArchived ? 'Chat archived' : 'Chat unarchived');
         }
      } else {
         showErrorToast(response?.message);
      }
   } catch (error) {
      consoleLog(error, 'err');
   }
};
